/**
 * Invites: people join with Google or Microsoft through a link that works once. The secret
 * is only in the link's fragment, shown once; the hub keeps its hash. A link invite adds a
 * sign-in to an existing member, and only an account with that proven email can redeem it.
 */
import { Link2, MailPlus, Plus, Trash2, TriangleAlert } from '@orgus/ui/icons';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { ROLE_RANK, roleAtLeast } from '@orgus/protocol';
import type { CreateInviteResponse, InviteInfo, Member, Role } from '@orgus/protocol';
import { Button, ConfirmDialog, DataTable, Dialog, EmptyState, IconButton, Input, Panel, RelativeTime, Select, StatusPill, Tooltip, cn, formatAbsolute, toast } from '@orgus/ui';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import type { Column } from '@orgus/ui';
import { authClient } from './client.ts';
import { CopyField } from './CopyField.tsx';
import { INVITE_HOURS, ROLE_LABEL, ROLE_VALUE_CLASS, grantableRoles, inviteStatusOf, memberHandle, roleOptions } from './labels.ts';

const EMAIL = /^[^\s@]{1,64}@[^\s@]+\.[^\s@]{2,}$/;

/** Null when `v` looks like an email address. */
export function checkEmail(v: string): string | null {
  if (!v) return 'Type the email address the person signs in with.';
  if (v.length > 254 || !EMAIL.test(v)) return 'That is not an email address. For example alice@acme.com.';
  return null;
}

/** The lifetimes an invite for `role` may have. */
export function inviteHourOptions(role: Role) {
  return INVITE_HOURS.map((h) => ({ value: h.value, label: h.label, disabled: ROLE_RANK[role] > ROLE_RANK[h.maxRole] }));
}

/** The one-time view of a new invite link. */
export function InviteLinkResult({ res }: { res: CreateInviteResponse }) {
  const i = res.info;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-base text-fg">
        Send this link to <span className="font-medium">{i.email}</span>. It works once, until {formatAbsolute(i.expiresAt)}. They confirm on the page before anything changes.
      </p>
      <CopyField value={res.url} label="Invite link" />
      <div className="flex items-start gap-3 rounded-control border border-status-attention-line bg-status-attention-soft px-3 py-2 text-sm">
        <TriangleAlert className="mt-1 size-4 shrink-0 text-status-attention" aria-hidden />
        <p className="text-fg-muted">
          {i.kind === 'link'
            ? `This is the only time the link is shown. Only a Google or Microsoft account that proves the address ${i.email} can redeem it, and it adds a sign-in to ${i.targetLogin ?? 'the member'}.`
            : `This is the only time the link is shown. The account used must have the address ${i.email}.`}
        </p>
      </div>
    </div>
  );
}

export function InviteDialog({ open, onOpenChange, myRole, onCreated }: { open: boolean; onOpenChange: (v: boolean) => void; myRole: Role; onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [hours, setHours] = useState('72');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<CreateInviteResponse | null>(null);
  const close = (v: boolean) => {
    onOpenChange(v);
    if (!v)
      setTimeout(() => {
        setEmail('');
        setError(null);
        setCreated(null);
      }, 200);
  };
  const pickRole = (r: Role) => {
    setRole(r);
    if (roleAtLeast(r, 'admin') && Number(hours) > 72) setHours('72');
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    const problem = checkEmail(v);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      setCreated(await authClient.createInvite({ email: v, role, hours: Number(hours) }));
      onCreated();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };
  if (created) {
    return (
      <Dialog open={open} onOpenChange={close} title="Copy the invite link now" size="md" footer={<Button variant="primary" onClick={() => close(false)}>Done</Button>}>
        <InviteLinkResult res={created} />
      </Dialog>
    );
  }
  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title="Invite someone"
      description="They join with the Google or Microsoft account for this address, through a link that works once."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="new-invite" loading={busy}>
            Create invite link
          </Button>
        </>
      }
    >
      <form id="new-invite" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Input label="Email" placeholder="alice@acme.com" type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={error ?? undefined} autoComplete="off" spellCheck={false} autoFocus />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Role" hint="At most your own role." value={role} onValueChange={(v) => pickRole(v as Role)} options={roleOptions(grantableRoles(myRole))} />
          <Select label="Link works for" hint={roleAtLeast(role, 'admin') ? 'Admin and owner invites: at most 72 hours.' : undefined} value={hours} onValueChange={setHours} options={inviteHourOptions(role)} />
        </div>
      </form>
    </Dialog>
  );
}

/** Add a Google or Microsoft sign-in to an existing member through a link. */
export function LinkInviteDialog({ member, onOpenChange, onCreated }: { member: Member | null; onOpenChange: (v: boolean) => void; onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [hours, setHours] = useState('72');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<CreateInviteResponse | null>(null);
  const [forId, setForId] = useState<string | null>(null);
  const high = !!member && roleAtLeast(member.role, 'admin');
  if (member && forId !== member.id) {
    setForId(member.id);
    setEmail(member.email ?? '');
    setHours(high ? '24' : '72');
    setError(null);
    setCreated(null);
  }
  const close = (v: boolean) => {
    onOpenChange(v);
    if (!v) setTimeout(() => setForId(null), 200);
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!member) return;
    const v = email.trim().toLowerCase();
    const problem = checkEmail(v);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      setCreated(await authClient.createLinkInvite({ memberId: member.id, email: v, hours: Number(hours) }));
      onCreated();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };
  const open = !!member;
  if (created) {
    return (
      <Dialog open={open} onOpenChange={close} title="Copy the link now" size="md" footer={<Button variant="primary" onClick={() => close(false)}>Done</Button>}>
        <InviteLinkResult res={created} />
      </Dialog>
    );
  }
  const options = high ? [{ value: '24', label: '24 hours' }] : [
    { value: '24', label: '24 hours' },
    { value: '72', label: '72 hours' },
  ];
  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={member ? `Add a sign-in method for ${member.name ?? member.login}` : ''}
      description="They sign in with the Google or Microsoft account whose proven email matches exactly."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="new-link-invite" loading={busy}>
            Create link
          </Button>
        </>
      }
    >
      <form id="new-link-invite" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Input
          label="Email of the account to add"
          hint={member?.provider === 'github' ? 'GitHub does not share a proven email: type the address of their Google or Microsoft account.' : undefined}
          placeholder="alice@acme.com"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={error ?? undefined}
          autoComplete="off"
          spellCheck={false}
          autoFocus
        />
        <Select label="Link works for" hint={high ? 'Admins and owners: at most 24 hours.' : undefined} value={hours} onValueChange={setHours} options={options} />
        <p className="text-sm text-fg-subtle">Personal Microsoft accounts link themselves in Settings, Sign-in.</p>
      </form>
    </Dialog>
  );
}

function RoleCell({ i }: { i: InviteInfo }) {
  const lower = i.effectiveRole && i.effectiveRole !== i.role;
  return (
    <span className="flex flex-wrap items-center gap-x-2">
      <span className={ROLE_VALUE_CLASS}>{ROLE_LABEL[i.effectiveRole ?? i.role]}</span>
      {lower && (
        <Tooltip content={`Created as ${ROLE_LABEL[i.role]}, but ${i.createdBy} can no longer give that role. Accepting gives ${ROLE_LABEL[i.effectiveRole!]}.`}>
          <span tabIndex={0} className="text-caption text-fg-subtle">
            was {ROLE_LABEL[i.role]}
          </span>
        </Tooltip>
      )}
    </span>
  );
}

export function InvitesPanel({ invites, members, myRole, canInvite, onChanged }: { invites: InviteInfo[]; members: Member[]; myRole: Role; canInvite: boolean; onChanged: () => void }) {
  const [inviting, setInviting] = useState(false);
  const [revoking, setRevoking] = useState<InviteInfo | null>(null);
  const pending = invites.filter((i) => i.status === 'pending').length;
  const target = (i: InviteInfo) => members.find((m) => m.id === i.memberId);
  const narrow = useMediaQuery(NARROW_QUERY);
  const columns: Column<InviteInfo>[] = [
    {
      key: 'email',
      header: 'Invite',
      grow: true,
      text: (i) => i.email,
      sortValue: (i) => i.email,
      cell: (i) => (
        <span className="flex min-w-0 items-center gap-2">
          {i.kind === 'link' ? <Link2 className="size-4 shrink-0 text-fg-subtle" aria-label="Adds a sign-in" /> : <MailPlus className="size-4 shrink-0 text-fg-subtle" aria-label="New member" />}
          <span className="min-w-0">
            <span className={cn('block font-medium text-fg', narrow ? 'break-all' : 'truncate')}>{i.email}</span>
            {narrow ? (
              /* Below 768 px Role and Status fold into the meta line, so the email keeps the row. */
              <span className="flex min-w-0 flex-wrap items-center gap-x-1 text-caption text-fg-subtle">
                <StatusPill status={inviteStatusOf(i).status} label={inviteStatusOf(i).label} variant="plain" className="text-caption" />
                <span aria-hidden>·</span>
                <span>{ROLE_LABEL[i.effectiveRole ?? i.role]}</span>
              </span>
            ) : (
              <span className="block truncate text-sm text-fg-subtle">
                {i.kind === 'link' ? `Adds a sign-in to ${(target(i) && memberHandle(target(i)!)) ?? i.targetLogin ?? 'a member'}` : 'Joins as a new member'}
              </span>
            )}
          </span>
        </span>
      ),
    },
    { key: 'role', header: 'Role', type: 'badge', hideBelow: 'md', sortValue: (i) => ROLE_RANK[i.effectiveRole ?? i.role], cell: (i) => <RoleCell i={i} /> },
    {
      key: 'status',
      header: 'Status',
      hideBelow: 'md',
      sortValue: (i) => i.status,
      cell: (i) => {
        const s = inviteStatusOf(i);
        return <StatusPill status={s.status} label={s.label} variant="plain" />;
      },
    },
    {
      key: 'expires',
      header: 'Expires',
      type: 'date',
      hideBelow: 'md',
      sortValue: (i) => i.expiresAt,
      cell: (i) => (i.status === 'used' ? <span className="text-sm text-fg-subtle">Used <RelativeTime ts={i.usedAt} /></span> : <RelativeTime ts={i.expiresAt} className="text-sm" />),
    },
    { key: 'by', header: 'Created by', hideBelow: 'lg', text: (i) => i.createdBy, cell: (i) => <span className="text-sm text-fg-muted">{i.createdBy}</span> },
  ];
  return (
    <Panel
      title="Invites"
      sub={pending ? `${pending} open` : undefined}
      icon={<MailPlus />}
      flush
      actions={
        canInvite ? (
          <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setInviting(true)}>
            Invite people
          </Button>
        ) : undefined
      }
    >
      <DataTable
        frameless
        label="Invites"
        rows={invites}
        rowKey={(i) => i.id}
        columns={columns}
        pageSize={false}
        initialSort={{ key: 'expires', dir: 'desc' }}
        empty={<EmptyState icon={<MailPlus />} title="No open invites" description="Invite someone by email to join with Google or Microsoft." />}
        rowActions={(i) => (canInvite && i.status === 'pending' ? <IconButton label={`Revoke the invite for ${i.email}`} icon={<Trash2 />} variant="ghost" size="sm" onClick={() => setRevoking(i)} /> : null)}
      />
      <p className="border-t border-border-subtle px-4 py-3 text-sm text-fg-subtle">Used, expired and revoked invites stay listed for 7 days.</p>
      <InviteDialog open={inviting} onOpenChange={setInviting} myRole={myRole} onCreated={onChanged} />
      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(v) => !v && setRevoking(null)}
        title={revoking ? `Revoke the invite for ${revoking.email}?` : ''}
        description="The link stops working now."
        confirmLabel="Revoke invite"
        onConfirm={async () => {
          if (!revoking) return;
          await authClient.revokeInvite(revoking.id);
          toast.success('The invite was revoked');
          setRevoking(null);
          onChanged();
        }}
      />
    </Panel>
  );
}
